import { useMemo, useState } from 'react';
import {
  Loader2,
  MapPin,
  Minus,
  Pencil,
  Plus,
  Search,
  Trash2
} from 'lucide-react';
import type {
  CreateStorageLocationPayload,
  StorageLocationNode
} from '../Types';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/shared/components/ui';
import { cn } from '@/shared/utils/helpers';
import {
  findStorageLocationNode,
  formatStorageLocationPath
} from '../Utils/StorageLocation.utils';
import { InventoryLocationSelect } from './InventoryLocationSelect';
import type { ListedInventoryItem } from './AddItemsForm';

type ItemFilter = 'all' | 'low' | 'out' | 'unassigned';

interface InventoryItemsPanelProps {
  items: ListedInventoryItem[];
  locations: StorageLocationNode[];
  defaultLocationId: string | null;
  deletingId?: string | null;
  updatingId?: string | null;
  /** Item currently loaded into the left add/edit form */
  editingItemId?: string | null;
  locationsLoading?: boolean;
  creatingLocation?: boolean;
  onDeleteItem: (item: ListedInventoryItem) => Promise<void>;
  onUpdateQuantity: (
    item: ListedInventoryItem,
    quantity: number
  ) => Promise<void>;
  onEditItem: (item: ListedInventoryItem) => void;
  onAssignLocation: (
    item: ListedInventoryItem,
    locationId: string
  ) => Promise<void>;
  onCreateLocation?: (
    payload: CreateStorageLocationPayload
  ) => Promise<StorageLocationNode>;
}

const isOutOfStock = (item: ListedInventoryItem) =>
  Number(item.quantityOnHand ?? 0) <= 0;

const isLowStock = (item: ListedInventoryItem) => {
  const quantity = Number(item.quantityOnHand ?? 0);
  const minimum = Number(item.parLevel ?? 0);
  return quantity > 0 && minimum > 0 && quantity < minimum;
};

const getDisplayStockUnit = (unit: string): string => {
  const trimmed = unit.trim();
  // Browser/test placeholders such as "Val 233" are not inventory units.
  return /^val(?:ue)?(?:\s|$)/i.test(trimmed) ? '' : trimmed;
};

export const InventoryItemsPanel = ({
  items,
  locations,
  defaultLocationId,
  deletingId = null,
  updatingId = null,
  editingItemId = null,
  locationsLoading = false,
  creatingLocation = false,
  onDeleteItem,
  onUpdateQuantity,
  onEditItem,
  onAssignLocation,
  onCreateLocation
}: InventoryItemsPanelProps) => {
  const [filter, setFilter] = useState<ItemFilter>('all');
  const [search, setSearch] = useState('');
  const [assigningItemId, setAssigningItemId] = useState<string | null>(null);
  const [pendingDeleteItem, setPendingDeleteItem] =
    useState<ListedInventoryItem | null>(null);

  const isConfirmingDelete = Boolean(
    pendingDeleteItem && deletingId === pendingDeleteItem.localId
  );

  const closeDeleteConfirm = () => {
    if (isConfirmingDelete) return;
    setPendingDeleteItem(null);
  };

  const confirmDelete = async () => {
    if (!pendingDeleteItem) return;
    try {
      await onDeleteItem(pendingDeleteItem);
      setPendingDeleteItem(null);
    } catch {
      // Keep the dialog open so the user can retry or cancel.
    }
  };

  const counts = useMemo(
    () => ({
      all: items.length,
      low: items.filter(isLowStock).length,
      out: items.filter(isOutOfStock).length,
      unassigned: items.filter(
        item => !item.locationId || item.locationId === defaultLocationId
      ).length
    }),
    [defaultLocationId, items]
  );

  const filteredItems = useMemo(() => {
    const query = search.trim().toLowerCase();
    return items.filter(item => {
      if (query && !item.name.toLowerCase().includes(query)) return false;
      if (filter === 'low') return isLowStock(item);
      if (filter === 'out') return isOutOfStock(item);
      if (filter === 'unassigned') {
        return !item.locationId || item.locationId === defaultLocationId;
      }
      return true;
    });
  }, [defaultLocationId, filter, items, search]);

  const filters: Array<{ id: ItemFilter; label: string }> = [
    { id: 'all', label: 'All' },
    { id: 'low', label: 'Low Stock' },
    { id: 'out', label: 'Out of Stock' },
    { id: 'unassigned', label: 'Unassigned' }
  ];

  return (
    <section className="flex h-full min-h-0 min-w-0 flex-col overflow-hidden rounded-lg border border-[#e7e9ee] bg-white shadow-sm">
      <div className="shrink-0 border-b border-[#e7e9ee] px-4 py-3">
        <h2 className="text-sm font-semibold text-[#171717]">Your items</h2>
        <p className="mt-0.5 text-[11px] text-[#8a8f96]">
          Storage and suppliers are assigned in the next steps.
        </p>
      </div>

      <div className="shrink-0 space-y-2 border-b border-[#e7e9ee] p-3">
        <div className="flex flex-wrap gap-1.5">
          {filters.map(itemFilter => (
            <button
              key={itemFilter.id}
              type="button"
              onClick={() => setFilter(itemFilter.id)}
              className={cn(
                'rounded-full border px-2.5 py-1 text-[11px] font-semibold transition',
                filter === itemFilter.id
                  ? 'border-[#087f8c] bg-[#087f8c] text-white'
                  : 'border-[#e1e4e8] bg-white text-[#5f646b] hover:border-[#087f8c]/50'
              )}
            >
              {itemFilter.label} ({counts[itemFilter.id]})
            </button>
          ))}
        </div>
        <div className="relative">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[#8a8f96]" />
          <input
            value={search}
            onChange={event => setSearch(event.target.value)}
            placeholder="Search items"
            className="h-8 w-full rounded-md border border-[#e7e9ee] bg-white pl-8 pr-3 text-xs outline-none focus:border-[#087f8c] focus:ring-2 focus:ring-[#087f8c]/15"
          />
        </div>
      </div>

      <div className="min-h-0 max-h-[min(68vh,620px)] flex-1 space-y-2 overflow-y-auto bg-[#f7f8f9] p-3 lg:max-h-none">
        {filteredItems.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#dfe3e7] bg-white px-4 py-8 text-center text-sm text-[#6f747c]">
            {items.length === 0
              ? 'No items yet. Add your first item using the form.'
              : 'No items match this filter.'}
          </div>
        ) : (
          filteredItems.map(item => {
            const quantity = Math.max(0, Number(item.quantityOnHand ?? 0));
            const isBusy = updatingId === item.localId;
            const isEditing = editingItemId === item.localId;
            const displayStockUnit = getDisplayStockUnit(item.stockUnit);
            const locationLeaf = item.locationId
              ? findStorageLocationNode(locations, item.locationId)?.name ??
                formatStorageLocationPath(locations, item.locationId)
              : null;
            const isUnassigned =
              !item.locationId || item.locationId === defaultLocationId;

            return (
              <article
                key={item.localId}
                className={cn(
                  'rounded-lg border bg-white p-3 shadow-sm transition',
                  isEditing
                    ? 'border-[#087f8c] ring-2 ring-[#087f8c]/15'
                    : item.categorySuggested
                      ? 'border-[#087f8c]/35 bg-[#f3fbfc]'
                      : 'border-[#e7e9ee]'
                )}
              >
                <div className="flex items-center gap-2">
                  <div className="min-w-0 flex-1">
                    <h3 className="min-w-0 text-sm font-semibold text-[#171717]">
                      <button
                        type="button"
                        onClick={() => onEditItem(item)}
                        title="Edit in form"
                        className="block max-w-full truncate text-left hover:text-[#087f8c] hover:underline"
                      >
                        {item.name}
                      </button>
                    </h3>
                    <p className="mt-0.5 text-[11px] text-[#6f747c]">
                      Min: {item.parLevel ?? 0}
                      {displayStockUnit ? ` ${displayStockUnit}` : ''}
                    </p>
                    {item.categoryName ? (
                      <p className="mt-1 flex flex-wrap items-center gap-1.5 text-[11px]">
                        <span
                          className={cn(
                            'inline-flex items-center rounded px-1.5 py-0.5 font-medium',
                            item.categorySuggested
                              ? 'bg-[#087f8c]/12 text-[#066873]'
                              : 'bg-[#f0f2f5] text-[#5f646b]'
                          )}
                        >
                          {item.categoryName}
                        </span>
                        {item.categorySuggested ? (
                          <span className="rounded bg-[#087f8c]/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-[#087f8c]">
                            AI suggested
                          </span>
                        ) : null}
                      </p>
                    ) : null}
                    <button
                      type="button"
                      onClick={() =>
                        setAssigningItemId(prev =>
                          prev === item.localId ? null : item.localId
                        )
                      }
                      className={cn(
                        'mt-1 inline-flex items-center gap-1 text-[11px] font-semibold hover:underline',
                        isUnassigned ? 'text-[#d16b68]' : 'text-[#087f8c]'
                      )}
                    >
                      <MapPin className="h-3 w-3" />
                      {isUnassigned
                        ? 'Assign Position'
                        : locationLeaf || 'Change Position'}
                    </button>
                  </div>

                  <div className="flex shrink-0 items-center gap-1">
                    <button
                      type="button"
                      disabled={isBusy || quantity <= 0}
                      onClick={() => {
                        void onUpdateQuantity(item, quantity - 1);
                      }}
                      className="grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white disabled:opacity-40"
                      aria-label={`Decrease ${item.name}`}
                    >
                      <Minus className="h-3.5 w-3.5" />
                    </button>
                    <div
                      title={`${quantity}${displayStockUnit ? ` ${displayStockUnit}` : ''}`}
                      className="flex h-8 w-[78px] shrink-0 items-center justify-center gap-1 overflow-hidden rounded-md border border-[#dfe3e7] bg-white px-1.5 text-xs font-bold tabular-nums text-[#087f8c]"
                    >
                      {isBusy ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <>
                          <span className="shrink-0">{quantity}</span>
                          {displayStockUnit && (
                            <span className="min-w-0 truncate font-medium text-[#6f747c]">
                              {displayStockUnit}
                            </span>
                          )}
                        </>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={isBusy}
                      onClick={() => {
                        void onUpdateQuantity(item, quantity + 1);
                      }}
                      className="grid h-8 w-8 place-items-center rounded-md bg-[#087f8c] text-white disabled:opacity-40"
                      aria-label={`Increase ${item.name}`}
                    >
                      <Plus className="h-3.5 w-3.5" />
                    </button>
                    <div className="ml-0.5 flex flex-col gap-1">
                      <button
                        type="button"
                        onClick={() => onEditItem(item)}
                        className={cn(
                          'grid h-7 w-7 place-items-center rounded-md border transition',
                          isEditing
                            ? 'border-[#087f8c] bg-[#087f8c]/10 text-[#087f8c]'
                            : 'border-[#e7e9ee] bg-white text-[#9a9fa6] hover:border-[#087f8c]/40 hover:bg-[#087f8c]/10 hover:text-[#087f8c]'
                        )}
                        aria-label={`Edit ${item.name}`}
                        title="Edit in form"
                      >
                        <Pencil className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        disabled={deletingId === item.localId}
                        onClick={() => setPendingDeleteItem(item)}
                        className="grid h-7 w-7 place-items-center rounded-md border border-[#e7e9ee] bg-white text-[#9a9fa6] transition hover:border-red-200 hover:bg-red-50 hover:text-red-600 disabled:opacity-40"
                        aria-label={`Remove ${item.name}`}
                        title="Remove item"
                      >
                        {deletingId === item.localId ? (
                          <Loader2 className="h-3.5 w-3.5 animate-spin" />
                        ) : (
                          <Trash2 className="h-3.5 w-3.5" />
                        )}
                      </button>
                    </div>
                  </div>
                </div>

                {assigningItemId === item.localId && (
                  <div className="mt-2 border-t border-[#edf0f2] pt-2">
                    <InventoryLocationSelect
                      compact
                      value={item.locationId}
                      locations={locations}
                      disabled={isBusy || locationsLoading}
                      canCreateLocation={Boolean(onCreateLocation)}
                      creatingLocation={creatingLocation}
                      onCreateLocation={onCreateLocation}
                      onChange={locationId => {
                        if (!locationId || locationId === item.locationId) {
                          setAssigningItemId(null);
                          return;
                        }
                        void onAssignLocation(item, locationId).finally(() =>
                          setAssigningItemId(null)
                        );
                      }}
                    />
                  </div>
                )}
              </article>
            );
          })
        )}
      </div>

      <Dialog
        open={Boolean(pendingDeleteItem)}
        onOpenChange={open => {
          if (!open) closeDeleteConfirm();
        }}
      >
        <DialogContent
          onClose={closeDeleteConfirm}
          className="max-w-sm border-[#e7e9ee] bg-white"
        >
          <DialogHeader>
            <DialogTitle>Remove item?</DialogTitle>
            <DialogDescription>
              {pendingDeleteItem
                ? `“${pendingDeleteItem.name}” will be removed from this location’s inventory.`
                : 'This item will be removed from inventory.'}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0">
            <button
              type="button"
              disabled={isConfirmingDelete}
              onClick={closeDeleteConfirm}
              className="inline-flex h-9 items-center justify-center rounded-md border border-[#e7e9ee] bg-white px-3 text-sm font-semibold text-[#6f747c] transition hover:bg-[#f0f2f5] hover:text-[#171717] disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={isConfirmingDelete}
              onClick={() => {
                void confirmDelete();
              }}
              className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md bg-red-600 px-3 text-sm font-semibold text-white transition hover:bg-red-700 disabled:opacity-60"
            >
              {isConfirmingDelete && (
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
              )}
              Remove
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};
